import {Injectable} from '@angular/core';
import {WizardInterfaces} from '../../wizard.namespace';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {MethodsProvider} from '@providers/methods/methods.provider';
import {ObjectsProvider} from '@providers/objects/objects.provider';
import StdDocument = WizardInterfaces.StdDocument;
import {WizardNestedFormService} from './abstract/wizard-nested-form-service';
import {WizardFormEventsService} from './wizard-form-events.service';
import {takeUntil} from 'rxjs/operators';
import {TechnicalDocument, TechnicalDocumentsProvider} from '@providers/technical-documents/technical-documents.provider';

@Injectable()
export class NtdService extends WizardNestedFormService {
    formCommon: FormGroup;
    formMethods: FormGroup;
    formObjects: FormGroup;

    private commonControl: FormControl;
    private methodsGroup: FormGroup;
    private objectsGroup: FormGroup;

    private rawDocumentsWithObjects: TechnicalDocument[] = [];
    private rawDocumentsWithMethods: TechnicalDocument[] = [];

    constructor(
        private wizardFormEvents: WizardFormEventsService,
        private documentsProvider: TechnicalDocumentsProvider,
        private methodsProvider: MethodsProvider,
        private objectsProvider: ObjectsProvider,
    ) {
        super();
    }

    init() {
        this.rawDocumentsWithObjects = this.documentsProvider.getDocumentsWithObject();
        this.rawDocumentsWithMethods = this.documentsProvider.getDocumentsWithMethod();

        this.wizardFormEvents.attestationChange$.pipe(
            takeUntil(this.destroyed$)
        ).subscribe((value) => {
            this.setObjectsStd(value);
            this.setMethodsStd(value);
        });

        this.setCommonStd();
    }

    restoreValidators() {
        this.resetForms();
    }

    private setObjectsStd(attestationValue: WizardInterfaces.AttestationValue[]) {
        const docsByObjects = this.getObjectIdsForDoc(attestationValue);
        this.getDocsByObjectsIds(docsByObjects);
        this.setDocumentByObjectsToForm(docsByObjects);
    }

    private getObjectIdsForDoc(attestationValue: WizardInterfaces.AttestationValue[]): DocsByObjects {
        const docsByObjects: DocsByObjects = {};
        /**
         * Получаем ids выбранных объектов по всем областям аттестации
         */
        const idsToSkip: number[] = [];
        /**
         * Собираем id всех объектов выбранной области аттестации и проходимся по ним
         */
        attestationValue.reduce((acc: number[], value: WizardInterfaces.AttestationValue) => {
            acc.push(...value.objects.map(o => +o.id));
            return acc;
        }, []).forEach(id => {
            /**
             * Если этот Id уже был учтен, то пропускаем его обработку
             */
            if (idsToSkip.indexOf(id) >= 0) {
                return;
            }

            const rootObject = this.objectsProvider.getRootById(id);
            const object = this.objectsProvider.getById(id);

            if (!rootObject || !rootObject) {
                idsToSkip.push(id);
                return;
            }

            if (!docsByObjects[rootObject.title]) {
                docsByObjects[rootObject.title] = {
                    objectsIds: [],
                    docs: []
                };
            }

            if (rootObject.id === id) {
                /**
                 * Если это id для всего объекта, то оставим его (return true) и не будем обрабатывать все его подобъекты
                 */
                const idsForObject = this.objectsProvider.getIdsForRootAndChild(rootObject);
                idsToSkip.push(...idsForObject);
                docsByObjects[rootObject.title].objectsIds = idsForObject;
            } else {
                /**
                 * @TODO при вложенности объектов более трех нужен будет метод получения всех родителей для дочернего id,
                 * но пока и так сойдет.
                 * Еще по красоте проверять бы, есть ли такой объект уже добавленным или нет, но на конечный рассчет не повляет тот факт,
                 * если один id будет добавлен много раз
                 */
                const ids = this.objectsProvider.getIdsForRootAndChild(object);
                docsByObjects[rootObject.title].objectsIds.push(object.parent_id);
                docsByObjects[rootObject.title].objectsIds.push(rootObject.id);
                docsByObjects[rootObject.title].objectsIds.push(...ids);
                idsToSkip.push(...ids);
            }
        });

        return docsByObjects;
    }

    private getDocsByObjectsIds(docsByObjects: DocsByObjects) {
        Object.keys(docsByObjects).forEach(title => {
            docsByObjects[title].docs = this.rawDocumentsWithObjects.filter(doc => docsByObjects[title].objectsIds.includes(doc.object_id));
        });
    }

    private setMethodsStd(attestationValue: WizardInterfaces.AttestationValue[]) {
        const docsByMethods = this.selectDocumentsForAttestation(attestationValue);
        this.setDocumentsByMethodsToForm(docsByMethods);
    }

    private selectDocumentsForAttestation(attestation: WizardInterfaces.AttestationValue[]): DocsByMethods {

        const result: DocsByMethods = {};

        attestation.forEach(att => {
            const method = this.methodsProvider.getByShortTitle(att.key);
            if (!method) {
                return;
            }

            /**
             * Выбираем id методов, которым нам нужны.
             * Если метод выбран метод целиком, то берем документы для этого метода и всех его подметодов,
             * а если выбрана только часть подметодов, то берем документы для родитекльского метода и выбранных подметодов
             */
            const idsToCheck = [+method.id];
            if (att.submethods.length) {
                idsToCheck.push(...att.submethods.map(m => +m.id));
            } else {
                if (method.methods && method.methods.length) {
                    idsToCheck.push(...method.methods.map(m => +m.id));
                }
            }

            result[method.title] = this.rawDocumentsWithMethods.filter(doc => idsToCheck.includes(+doc.method_id));
        });

        return result;
    }

    private setCommonStd() {
        const items = this.documentsProvider.getCommonDocuments();
        this.commonControl.patchValue(items.map(std => std.id));
    }

    /**
     * Очистить значения форм для НТД
     */
    private resetForms() {
        this.removeListControl(this.formCommon);
        this.formCommon.addControl('list', new FormControl([]));
        this.commonControl = this.formCommon.get('list') as FormControl;

        this.resetMethodsForm();
        this.resetObjectsForm();
    }

    private resetObjectsForm() {
        this.removeListControl(this.formObjects);
        this.formObjects.addControl('list', new FormGroup({}));
        this.objectsGroup = this.formObjects.get('list') as FormGroup;
    }

    private resetMethodsForm() {
        this.removeListControl(this.formMethods);
        this.formMethods.addControl('list', new FormGroup({}));
        this.methodsGroup = this.formMethods.get('list') as FormGroup;
    }

    /**
     * Удалить из форм существующие записи по НТД
     * @param form
     */
    private removeListControl(form: FormGroup) {
        if (form.contains('list')) {
            form.removeControl('list');
        }
    }

    private setDocumentByObjectsToForm(docsByObjects: DocsByObjects) {
        this.resetObjectsForm();
        Object.keys(docsByObjects).forEach((key: string) => {
            this.objectsGroup.setControl(key, new FormControl(docsByObjects[key].docs.map(std => +std.id)));
        });
    }

    private setDocumentsByMethodsToForm(docs: DocsByMethods) {
        this.resetMethodsForm();
        Object.keys(docs).forEach((key: string) => {
           this.methodsGroup.setControl(key, new FormControl(docs[key].map(std => +std.id)));
        });
    }
}

interface DocsByObjects {
    [key: string]: {
        objectsIds: number[],
        docs: TechnicalDocument[],
    };
}

interface DocsByMethods {
    [key: string]: TechnicalDocument[];
}
